import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../fiftyTwoWeekHighArticle.css";

// Humanized-voice, Medium-style rewrite of the 52-week-high evidence
// doc, signed off 2026-10-28 after review on an isolated preview build.
// Rendered inline by MarketMythsPage.tsx's EvidenceReader at the SAME
// URL the old SimpleMarkdown-rendered doc used to occupy
// (/market-myths?evidence=fifty-two-week-high) -- a bespoke React
// component, same precedent as SuePeadPage/MacroFactorsPage, not a new
// generic template.
//
// Every number below is sourced to the live feature_library row for
// pct_52w_high, re-verified fresh before writing this (not carried over
// from the earlier AI-toned doc): ic=0.04307770 (rounds to 0.043),
// t_stat=13.407433776434697 (rounds to 13.41), n_dates=2222,
// per_segment_ic_cap={micro:0.0053, small:0.0252, mid:0.0142,
// large:0.0181} (all positive), decile_monotonicity=-0.17575757575757575
// (rounds to -0.176), stability="stable sign across sub-periods",
// blessed_at=2026-08-08.
//
// DECILE CHART, 2026-10-28 revision: the per-segment-IC chart this page
// shipped with originally showed only the flattering half (4 positive
// bars) -- the real asterisk, the non-monotonic decile sort, had no
// visual. No decile-level return series existed anywhere in this
// codebase (only the single decile_monotonicity summary stat was ever
// persisted), so one was computed and archived:
// backend/pct_52wh_decile_chart.py (read-only -- bars_as_of + a plain
// feature_values SELECT, writes one JSON) -> data/pct_52wh_decile_means
// .json. Per-date cross-sectional decile bucketing (matching app/
// derived/lab.py's own decile_monotonicity() bucket-split method),
// bucket means averaged ACROSS 2,230 qualifying dates (per-date-then-
// average, matching how the real published IC was computed -- NOT the
// pooled-panel basis this project's own cross_sectional_ic() docstring
// warns is confounded). The resulting monotonicity check (-0.1394) lands
// the same sign and a comparable magnitude to the already-published
// -0.1758 (computed on a different, pooled basis) -- a real, independent
// cross-check, not assumed to match exactly.
const DECILE_MEANS: number[] = [
  0.00085, 0.001873, 0.000778, 0.000243, -0.000019,
  0.000112, 0.000337, 0.00011, 0.000776, 0.003536,
];

function DecileChart() {
  const width = 640;
  const height = 280;
  const margin = { top: 16, right: 16, bottom: 52, left: 48 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;
  const maxAbs = Math.max(...DECILE_MEANS.map((v) => Math.abs(v)));
  const barW = plotW / DECILE_MEANS.length;
  const zeroY = margin.top + plotH / 2;
  const scale = (plotH / 2 - 10) / maxAbs;

  return (
    <figure className="fiftyk-chart">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="Forward 5-day return by decile of proximity to 52-week high: mostly positive, strongest in the top decile, but not a clean staircase -- decile 5 dips slightly negative">
        <line x1={margin.left} y1={zeroY} x2={width - margin.right} y2={zeroY} className="fiftyk-chart-zeroline" />
        {DECILE_MEANS.map((v, i) => {
          const x = margin.left + i * barW;
          const h = Math.abs(v) * scale;
          const y = v >= 0 ? zeroY - h : zeroY;
          return (
            <g key={i}>
              <rect x={x + barW * 0.18} y={y} width={barW * 0.64} height={Math.max(h, 1)}
                    className={v >= 0 ? "fiftyk-chart-bar" : "fiftyk-chart-bar-neg"} />
              <text x={x + barW / 2} y={height - margin.bottom + 16} textAnchor="middle" className="fiftyk-chart-label">
                {i + 1}
              </text>
            </g>
          );
        })}
        <text x={margin.left} y={height - margin.bottom + 36} className="fiftyk-chart-axis-note">
          Decile 1 = furthest from 52-week high &rarr; Decile 10 = closest
        </text>
      </svg>
      <figcaption>
        Average forward 5-trading-day return by decile of closeness to a stock's own 52-week high. Mostly up,
        but not a clean staircase — the tilt is real (decile 10 is clearly the strongest), but the ordering in
        between isn't tidy, and decile 5 dips slightly negative.
      </figcaption>
      <div className="fiftyk-asof">
        As of 2026&#8209;08&#8209;12 · re-validated on a slow cadence (quarterly), not daily — this is a dated
        snapshot, not a live feed.
      </div>
    </figure>
  );
}

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=fifty-two-week-high";

export function FiftyTwoWeekHighArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "Buying strength actually works — with an asterisk — Tickerpicker",
    description:
      "Stocks near their own 52-week high keep outperforming over the next week, in our own test of Indian " +
      "equities — a real, confirmed, two-decades-old market anomaly, with one honest wrinkle in how it sorts.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Buying strength actually works — with an asterisk",
      description:
        "A test of the 52-week-high momentum effect on Indian equities: a real, confirmed, segment-consistent " +
        "edge, with one honest caveat in the decile sort.",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="fiftyk-shell">
      <nav className="fiftyk-nav">
        <a href="/" className="fiftyk-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </nav>

      <article className="fiftyk-article">
        <div className="fiftyk-kicker">Market Myths: Tested — 52-week-high effect</div>
        <h1>Buying strength actually works — with an asterisk</h1>

        <p>
          Every trader gets told to buy what's already going up. Most wisdom like that falls apart the moment you
          test it. This one mostly doesn't.
        </p>

        <p>
          We measure how close each stock sits to its own 52-week high, then ask whether the ones near the top
          outrun the ones further down over the next week. Across 2,222 trading days of Indian equities, they do.
          The correlation is about 0.043 — which sounds like nothing, until you remember that what moves markets
          is faint and noisy by nature. With a t-statistic past 13 and the same positive sign in every size
          bucket we tested — micro-caps, small-caps, mid-caps, large-caps — it's roughly as sturdy as this kind
          of signal gets. And it holds across different stretches of history instead of hiding in one lucky
          year, which is where most "edges" quietly die.
        </p>

        <p>
          Here's the asterisk, though. Sort stocks into ten buckets by proximity-to-high and the returns don't
          climb in a tidy staircase.
        </p>

        <DecileChart />

        <blockquote className="fiftyk-pullquote">
          The top leans the right way, on average. You can't read "higher is always better" off it.
        </blockquote>

        <p>
          Decile ten — the stocks sitting closest to their own high — pulls clearly ahead of everything else.
          But the middle of the pack is a mess: returns wobble up and down bucket to bucket, and the fifth
          decile is actually slightly negative. Our monotonicity score comes out at −0.176, confirming what the
          chart shows by eye. So this is a tilt, not a law. We use it as one input among many feeding our
          models, never a standalone rule, and that messy middle is exactly why.
        </p>

        <p>
          For the lineage: this is the 52-week-high momentum effect George and Hwang documented in 2004, in
          U.S. markets. Satisfying to see it turn up intact, in a different market, two decades later.
        </p>

        <hr className="fiftyk-rule" />

        <details className="fiftyk-numbers">
          <summary>The full numbers</summary>
          <table>
            <tbody>
              <tr><td>Pooled correlation (IC)</td><td>+0.0431</td></tr>
              <tr><td>t-statistic</td><td>13.41</td></tr>
              <tr><td>Trading dates</td><td>2,222</td></tr>
              <tr><td>Decile monotonicity</td><td>−0.176</td></tr>
              <tr><td>Decile forward return (5d), deciles 1&ndash;10</td>
                  <td>+0.085% / +0.187% / +0.078% / +0.024% / &minus;0.002% / +0.011% / +0.034% / +0.011% / +0.078% / +0.354%</td></tr>
              <tr>
                <td>Decile dates used*</td>
                <td>2,230</td>
              </tr>
              <tr><td>Per-segment IC — micro-cap</td><td>+0.0053</td></tr>
              <tr><td>Per-segment IC — small-cap</td><td>+0.0252</td></tr>
              <tr><td>Per-segment IC — mid-cap</td><td>+0.0142</td></tr>
              <tr><td>Per-segment IC — large-cap</td><td>+0.0181</td></tr>
              <tr><td>Sub-period stability</td><td>stable sign across sub-periods</td></tr>
              <tr><td>Blessed into production</td><td>2026-08-08</td></tr>
            </tbody>
          </table>
          <p className="fiftyk-source">
            *2,230 vs. the 2,222 trading days cited above: the decile series was computed on the full
            model-eligible universe (2,070 securities), not the original blessing run's 294-security
            stratified sample (265 usable) — a different population, not a different method; the two counts
            aren't meant to match exactly.
          </p>
          <p className="fiftyk-source">
            Sources: <code>feature_library</code> (live production blessing record, <code>pct_52w_high</code>);
            decile series from <code>backend/pct_52wh_decile_chart.py</code> (read-only), archived at{" "}
            <code>data/pct_52wh_decile_means.json</code>. Citation: George, T. J., &amp; Hwang, C.&#8209;Y.
            (2004). The 52-Week High and Momentum Investing. <em>The Journal of Finance</em>, 59(5),
            2145&ndash;2176.
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
